import {DataTable as PeacockDataTable} from '@pepsico-ds/ui'
import './DataTable.scss'

const PLACEHOLDER_FILTERS = [{label: '', options: []}]

/**
 * @template {object} T
 * @typedef {import('@pepsico-ds/ui').IDataTableProps<T>} DataTableProps
 * @property {Array<{ columnKey: string; header: string | (() => import('react').ReactElement); cellData?: (ctx: import('@tanstack/react-table').CellContext<T, unknown>) => import('react').ReactElement; columnAlignment?: string; columnFilterPlaceholder?: string; dropdownFilterOptions?: { selection?: 'single' | 'multiple'; dropdownHeader?: string; disabled?: boolean; error?: boolean; }; filterVariant?: 'text' | 'dropdown' | 'date' | 'dateRange'; initialSize?: number; maxSize?: number; minSize?: number; enableSorting?: boolean; }>} columns Required column configuration.
 * @property {T[]} data Required row data. Keys should match column keys.
 * @property {number} [debounceDelay] Debounce delay in ms for search/filter. Defaults to 300.
 * @property {Record<number | string, boolean>} [initialRowSelection] Pre-selected rows on mount.
 * @property {import('@pepsico-ds/ui').IEmptyStateV2Props} [emptyStateProps] Empty state props.
 * @property {boolean} [enableSorting] Enables/disables sorting. Defaults to true.
 * @property {import('@pepsico-ds/ui').FilterProps[]} [filters] Toolbar dropdown filters (max 5). Required for reset button to appear.
 * @property {number} [allFiltersBadgeValue] Custom badge value for the "All Filters" button.
 * @property {() => void} [onAllFiltersClick] Callback when "All Filters" button is clicked.
 * @property {(row: import('@tanstack/react-table').Row<T>) => boolean} [getRowCanExpand] Override whether a row can be expanded.
 * @property {(originalRow: T, index: number) => undefined | T[]} [getSubRows] Access sub rows for nested rows.
 * @property {boolean} [isExpandable] When true, renders expandable rows.
 * @property {boolean} [isResizable] Enables column resizing.
 * @property {boolean} [isLoading] When true, renders loading state. Defaults to false.
 * @property {string} [maxHeight] Max height for sticky header mode (e.g. '70vh').
 * @property {boolean} [manualFiltering] When true, enables server-side filtering.
 * @property {boolean} [manualPagination] When true, enables server-side pagination.
 * @property {boolean} [manualSorting] When true, enables server-side sorting.
 * @property {string | import('react').ReactNode} [noDataTitleText] Text when table has no data.
 * @property {string | import('react').ReactNode} [noDataDescriptionText] Description when table has no data.
 * @property {(value: unknown, type: 'text' | 'dropdown' | 'date' | 'dateRange' | undefined, columnKey: string) => void} [onColumnFiltersChange] Callback when a column filter changes.
 * @property {(visibility: Record<string, boolean>) => void} [onColumnVisibilityChange] Callback when column visibility changes (requires showTableSettings).
 * @property {(order: string[]) => void} [onColumnOrderChange] Callback when column order changes (requires showTableSettings).
 * @property {(value: string) => void} [onGlobalSearchChange] Callback when global search changes.
 * @property {(newSelectedPage: number) => void} [onPageIndexChange] Callback when page changes.
 * @property {(newSelectedRowsPerPage: number) => void} [onRowsPerPageChange] Callback when rows per page changes.
 * @property {number} [pageIndex] Current page index (0-based).
 * @property {number} [pageSize] Number of rows per page.
 * @property {(row: import('@tanstack/react-table').Row<T>) => import('react').ReactNode} [renderSubComponent] Renders expanded row content.
 * @property {{ onFiltersReset?: () => void; text?: string }} [resetFilterButtonOptions] Reset filter button options. Only visible when filters prop has items.
 * @property {import('@pepsico-ds/ui').IButtonIconProps[]} [rowActions] Per-row action buttons (max 3 before "See more").
 * @property {(rows: Iterable<any, void, undefined>) => void} [rowExpandingHandler] Callback when row expanding changes.
 * @property {(selectedRows: import('@tanstack/react-table').Row<T>[], globalFilter?: string[], columnFilters?: import('@tanstack/react-table').ColumnFiltersState) => void} [rowSelectionStateUpdater] Callback when row selection changes. ⚠️ Has known bug with manualPagination=true + isLoading=true.
 * @property {string} [searchPlaceholder] Placeholder for the search input.
 * @property {'single' | 'multiple' | 'none'} [selection] Row selection mode.
 * @property {boolean} [parentRowSelection] When false, parent row excluded from selection. Defaults to true.
 * @property {number} [maxRowSelection] Max number of selectable rows.
 * @property {(selectedCount: number, maxLimit: number, isLimitReached: boolean) => void} [onMaxSelectionReached] Callback when max selection is reached.
 * @property {import('@pepsico-ds/ui').IButtonProps[]} [selectedRowsActions] Toolbar actions shown when rows are selected.
 * @property {boolean} [showColumnFilters] Show column filter inputs. Defaults to true.
 * @property {boolean} [showColumnVisibility] Show column visibility toggle.
 * @property {boolean} [showTableSettings] Show column settings popover (reorder + visibility).
 * @property {boolean} [showDescription] Show table description. Defaults to true.
 * @property {boolean} [showFilterButton] Show filter toggle button. Defaults to true.
 * @property {boolean} [showFilterToolbar] Initially show the filter toolbar. Defaults to false.
 * @property {boolean} [showMultiSortMenu] Show multi-column sort menu.
 * @property {boolean} [showPagination] Show pagination. Defaults to true.
 * @property {boolean} [showSearchOption] Show search input. Defaults to true.
 * @property {boolean} [showTableHeader] Show title, description and search. Defaults to true.
 * @property {boolean} [showTitle] Show table title. Defaults to true.
 * @property {'large' | 'medium' | 'small'} [size] Table size.
 * @property {({ column: string; direction: 'asc' | 'desc' }[]) => void} [sortingHandler] Callback when sorting changes (required when manualSorting=true).
 * @property {string} [tableDescription] Table description text.
 * @property {string} [tableHeader] Table title text.
 * @property {import('@pepsico-ds/ui').IButtonProps[] | import('@pepsico-ds/ui').IButtonMenuProps} [toolbarActions] Toolbar action buttons.
 * @property {number} [totalRowsCount] Total rows for manual pagination.
 * @property {import('@tanstack/react-table').ColumnPinningState} [columnPinningState] Column pinning state.
 * @property {boolean} [isDraggable] When true, renders draggable rows.
 * @property {(row: import('@tanstack/react-table').Row<T>, index: number) => void} [onDragStartHandler] Callback when drag starts.
 * @property {(draggedRow: import('@tanstack/react-table').Row<T>, targetIndex: number, draggedIndex: number) => void} [onDragEndHandler] Callback when drag ends.
 * @property {(table: import('@tanstack/react-table').Table<T>) => void} [onTableReady] Callback when table is initialized. Provides access to the tanstack Table instance.
 * @property {string} ['data-cy'] Optional Cypress test attribute.
 */

/**
 * Wrapper around Peacock DS DataTable.
 * Supports all Peacock DS DataTable props.
 * @template {object} T
 * @param {DataTableProps<T>} props
 */
export const DataTable = ({
  showTableHeader = false,
  showMultiSortMenu = false,
  showColumnVisibility = false,
  resetFilterButtonOptions,
  filters,
  showFilterToolbar,
  ...props
}) => {
  const hasReset = Boolean(resetFilterButtonOptions)
  return (
    <PeacockDataTable
      showTableHeader={showTableHeader}
      showMultiSortMenu={showMultiSortMenu}
      showColumnVisibility={showColumnVisibility}
      resetFilterButtonOptions={resetFilterButtonOptions}
      filters={hasReset ? (filters ?? PLACEHOLDER_FILTERS) : filters}
      showFilterToolbar={hasReset ? true : showFilterToolbar}
      {...props}
    />
  )
}
